import { UserRound, Sparkles } from "lucide-react";

/**
 * 사진이 없는 회원용 애니메이션 아바타
 * - 성별별 그라데이션 배경
 * - 부드러운 펄스/플로팅 모션 (CSS 키프레임)
 * - 회원 이름 첫 글자 표시 (옵션)
 */
export default function AnimatedAvatar({ name, gender, className = "" }) {
  const isFemale = gender === "female" || gender === "여성" || gender === "여자" || gender === "f";
  const initial = (name || "").trim().charAt(0).toUpperCase() || "?";

  const gradient = isFemale
    ? "from-rose-200 via-pink-300 to-fuchsia-300"
    : "from-sky-200 via-indigo-300 to-purple-300";
  const ringColor = isFemale ? "bg-rose-300/30" : "bg-indigo-300/30";
  const iconColor = isFemale ? "text-rose-50" : "text-sky-50";

  return (
    <div className={`relative w-full h-full overflow-hidden bg-gradient-to-br ${gradient} ${className}`}>
      {/* Background animated rings — larger to fill card */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className={`absolute w-64 h-64 rounded-full ${ringColor} animate-avatar-pulse-1`} />
        <div className={`absolute w-52 h-52 rounded-full ${ringColor} animate-avatar-pulse-2`} />
        <div className={`absolute w-40 h-40 rounded-full ${ringColor} animate-avatar-pulse-3`} />
      </div>

      {/* Sparkles in corners — bigger */}
      <Sparkles className={`absolute top-5 right-5 w-6 h-6 ${iconColor} opacity-60 animate-avatar-twinkle`} />
      <Sparkles className={`absolute bottom-8 left-7 w-5 h-5 ${iconColor} opacity-50 animate-avatar-twinkle-delay`} />

      {/* Center: large user silhouette + initial */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative flex flex-col items-center animate-avatar-float">
          <div className="relative">
            <UserRound className={`w-44 h-44 ${iconColor} drop-shadow-2xl`} strokeWidth={1.3} />
            {/* Initial letter overlay */}
            {initial !== "?" && (
              <div className="absolute inset-0 flex items-center justify-center pt-3">
                <span className="text-6xl font-bold text-white/90 drop-shadow-lg">{initial}</span>
              </div>
            )}
          </div>
          <p className="mt-3 text-[11px] uppercase tracking-[0.32em] text-white/75 font-semibold">
            New Coupling
          </p>
        </div>
      </div>

      {/* Bottom subtle gradient for text legibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent pointer-events-none" />

      {/* Inline CSS for animations (Tailwind doesn't have these built-in) */}
      <style>{`
        @keyframes avatarPulse1 {
          0%, 100% { transform: scale(1); opacity: 0.25; }
          50%      { transform: scale(1.15); opacity: 0.12; }
        }
        @keyframes avatarPulse2 {
          0%, 100% { transform: scale(0.95); opacity: 0.35; }
          50%      { transform: scale(1.1);  opacity: 0.18; }
        }
        @keyframes avatarPulse3 {
          0%, 100% { transform: scale(1); opacity: 0.45; }
          50%      { transform: scale(0.9); opacity: 0.25; }
        }
        @keyframes avatarFloat {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-6px); }
        }
        @keyframes avatarTwinkle {
          0%, 100% { opacity: 0.3; transform: scale(1) rotate(0); }
          50%      { opacity: 0.9; transform: scale(1.3) rotate(15deg); }
        }
        .animate-avatar-pulse-1 { animation: avatarPulse1 4.2s ease-in-out infinite; }
        .animate-avatar-pulse-2 { animation: avatarPulse2 5.5s ease-in-out infinite 0.6s; }
        .animate-avatar-pulse-3 { animation: avatarPulse3 6.8s ease-in-out infinite 1.2s; }
        .animate-avatar-float   { animation: avatarFloat 4s ease-in-out infinite; }
        .animate-avatar-twinkle { animation: avatarTwinkle 3s ease-in-out infinite; }
        .animate-avatar-twinkle-delay { animation: avatarTwinkle 3.5s ease-in-out infinite 1.2s; }
      `}</style>
    </div>
  );
}
